import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FerryIngredient,
  FlightIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { isFlightIngredient } from '@apps/main/modules/recipe/ingredients';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { FerryDetailsModalData } from '@apps/main/new-desktop/modules/ferryDetailsModal';
import type { FlightDetailsModalData } from '@apps/main/new-desktop/modules/flightDetailsModal';

import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
} from '@libs/search-service-smart-search';
import type {
  FerryIngredientResponse,
  FlightIngredientResponse,
  IngredientId,
  TransportAlternative,
  TransportLeg,
  TransportStaticInfo,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard } from '@libs/utils';

import type { HomeFlightData } from '../../../modules/airportSelector';
import type { ItineraryLocationSegment, LocationSegment } from '../models';
import { ItinerarySegmentType, ProductCardType } from '../models';

import {
  getAirportStaticInfo,
  getCarrierAndTransportationName,
  getCarrierInfo,
  getRouteInfo,
  getVehicleInfo,
} from './ferryDetailsModalLogic';

const { t } = i18n.global;

export function getChangeButtonText(productType: ProductCardType): string {
  switch (productType) {
    case ProductCardType.Activity:
      return t('planner.change_activity');
    case ProductCardType.Ferry:
      return t('planner.change_ferry');
    case ProductCardType.Flight:
      return t('planner.change_flights');
    case ProductCardType.Accommodation:
      return t('planner.change_hotel');
    default:
      exhaustiveGuard(productType, 'the product card type is not defined');
  }
}

export function getHomeFlightData(
  recipe: RecipeMainIngredient[]
): HomeFlightData | null {
  if (!recipe.length) {
    return null;
  }

  const firstIngredient = recipe[0];

  assertValue(
    firstIngredient,
    'tripOverviewStoreLogic.ts > getHomeFlightData > firstIngredient'
  );

  const outboundFlight = isFlightIngredient(firstIngredient)
    ? firstIngredient
    : null;

  const lastIngredient = recipe[recipe.length - 1];

  assertValue(
    lastIngredient,
    'tripOverviewStoreLogic.ts > getHomeFlightData > lastIngredient'
  );

  const homeboundFlight = isFlightIngredient(lastIngredient)
    ? lastIngredient
    : null;

  if (outboundFlight) {
    if (
      !outboundFlight.fromPort ||
      !outboundFlight.fromPortText ||
      !outboundFlight.fromPortDisplayText
    ) {
      return null;
    }

    return {
      airport: {
        iata: outboundFlight.fromPort,
        name: outboundFlight.fromPortText,
        displayingText: outboundFlight.fromPortDisplayText,
      },
      outboundIngredientId: outboundFlight.ingredientId,
      homeboundIngredientId:
        homeboundFlight?.toPort === outboundFlight.fromPort
          ? homeboundFlight?.ingredientId
          : undefined,
    };
  }

  if (homeboundFlight) {
    if (
      !homeboundFlight.toPort ||
      !homeboundFlight.toPortText ||
      !homeboundFlight.toPortDisplayText
    ) {
      return null;
    }

    return {
      airport: {
        iata: homeboundFlight.toPort,
        name: homeboundFlight.toPortText,
        displayingText: homeboundFlight.toPortDisplayText,
      },
      homeboundIngredientId: homeboundFlight.ingredientId,
    };
  }

  return null;
}

export function getFerryDetails(
  ingredientResponse: FerryIngredientResponse,
  staticInfo: TransportStaticInfo | undefined,
  ticketType: string | undefined,
  ferryVehicleTypes: TransportVehicle[] | undefined
): FerryDetailsModalData | undefined {
  if (!isAvailableFerryIngredientResponse(ingredientResponse)) {
    return;
  }

  const leg = ingredientResponse.legs[ingredientResponse.legIndex];
  if (!leg) {
    return;
  }

  return {
    departureText: getAirportStaticInfo(
      ingredientResponse.departureIata,
      staticInfo
    )?.name,
    arrivalText: getAirportStaticInfo(
      ingredientResponse.arrivalIata,
      staticInfo
    )?.name,

    arrivalDate: ingredientResponse.arrivalDate,
    departureDate: ingredientResponse.departureDate,

    price: ingredientResponse.totalPrice,
    addToBasketToken: ingredientResponse.addToBasketToken,

    segment: leg.segments.map((segment) => ({
      carrier: getCarrierInfo(segment.carrier, staticInfo),
      duration: segment.durationInMinutes,
      carrierAndTransportationName: getCarrierAndTransportationName(
        getCarrierInfo(segment.carrier, staticInfo)?.name,
        segment.transportationName
      ),
      routeInfo: getRouteInfo(
        getVehicleInfo(ferryVehicleTypes, segment.vehicles),
        ticketType,
        segment.isCabinMandatory
      ),
    })),
  };
}

export function getFlightDetails(
  ingredientResponse: FlightIngredientResponse,
  staticInfo: TransportStaticInfo | undefined
): FlightDetailsModalData | undefined {
  if (!isAvailableFlightIngredientResponse(ingredientResponse)) {
    if (ingredientResponse.alternativeSuggestion) {
      const leg =
        ingredientResponse.alternativeSuggestion.legs[
          ingredientResponse.alternativeSuggestion.legIndex
        ];

      if (!leg) {
        return;
      }

      // TODO: luggage when ready
      return {
        title: {
          fromAirport: getAirportStaticInfo(
            ingredientResponse.alternativeSuggestion.departureIata,
            staticInfo
          ),
          toAirport: getAirportStaticInfo(
            ingredientResponse.alternativeSuggestion.arrivalIata,
            staticInfo
          ),
        },

        segments: leg.segments.map((segment) => {
          const arrivalAirport = getAirportStaticInfo(
            segment.arrivalIata,
            staticInfo
          );
          const departureAirport = getAirportStaticInfo(
            segment.departureIata,
            staticInfo
          );
          return {
            arrivalDate: segment.arrivalDateTime,
            arrivalAirport,
            departureDate: segment.departureDateTime,
            departureAirport,
            carrier: getCarrierInfo(segment.carrier, staticInfo),
            operatingCarrier: getCarrierInfo(
              segment.operatingCarrier,
              staticInfo
            ),
            flightNumber: segment.transportId,
            duration: segment.durationInMinutes || 0,
            stopver: segment.layoverTimeUntilNextSegmentInMinutes,
          };
        }),
        price: ingredientResponse.alternativeSuggestion.totalPrice,
        isHomebound: ingredientResponse.alternativeSuggestion.legIndex === 1,
        addToBasketToken:
          ingredientResponse.alternativeSuggestion.addToBasketToken,
        arrivalDate: ingredientResponse.alternativeSuggestion.arrivalDate,
        departureDate: ingredientResponse.alternativeSuggestion.departureDate,
      };
    }
    return;
  }

  const leg = ingredientResponse.legs[ingredientResponse.legIndex];

  if (!leg) {
    return;
  }

  // TODO: luggage when ready
  return {
    title: {
      fromAirport: getAirportStaticInfo(
        ingredientResponse.departureIata,
        staticInfo
      ),
      toAirport: getAirportStaticInfo(
        ingredientResponse.arrivalIata,
        staticInfo
      ),
    },

    segments: leg.segments.map((segment) => {
      const arrivalAirport = getAirportStaticInfo(
        segment.arrivalIata,
        staticInfo
      );
      const departureAirport = getAirportStaticInfo(
        segment.departureIata,
        staticInfo
      );
      return {
        arrivalDate: segment.arrivalDateTime,
        arrivalAirport,
        departureDate: segment.departureDateTime,
        departureAirport,
        carrier: getCarrierInfo(segment.carrier, staticInfo),
        operatingCarrier: getCarrierInfo(segment.operatingCarrier, staticInfo),
        flightNumber: segment.transportId,
        duration: segment.durationInMinutes || 0,
        stopver: segment.layoverTimeUntilNextSegmentInMinutes,
      };
    }),
    price: ingredientResponse.totalPrice,
    isHomebound: ingredientResponse.legIndex === 1,
    addToBasketToken: ingredientResponse.addToBasketToken,
    arrivalDate: ingredientResponse.arrivalDate,
    departureDate: ingredientResponse.departureDate,
  };
}

export function updateFlightIngredientsWithAlternativeSuggestion(
  transportIngredientId: IngredientId,
  alternativeTransport: TransportAlternative,
  getTransportIngredient: (
    transportIngredientId: IngredientId
  ) => FlightIngredient | FerryIngredient
): void {
  const alternativeFlightResponseLeg =
    alternativeTransport.legs[alternativeTransport.legIndex];

  assertValue(alternativeFlightResponseLeg);

  const flightIngredient = getTransportIngredient(transportIngredientId);

  flightIngredient.transportIds = getAllTransportIdsFromLeg(
    alternativeFlightResponseLeg
  );
}

function getAllTransportIdsFromLeg(transportLeg: TransportLeg): string[] {
  return transportLeg.segments.flatMap((segment) => segment.transportId);
}

export function getAutocompleteTypeFromLocationSegment(
  segment: ItineraryLocationSegment
): AutoCompleteType.StartingPosition | AutoCompleteType.EndingPosition {
  return segment.segmentType === ItinerarySegmentType.StartLocation
    ? AutoCompleteType.StartingPosition
    : AutoCompleteType.EndingPosition;
}

export function getSearchTextFromLocationSegment(
  locationSegment: LocationSegment
): string {
  if (
    locationSegment.name === t('planner.your_position') ||
    locationSegment.name === t('planner.your_destination')
  ) {
    return '';
  }

  if (locationSegment.country) {
    return `${locationSegment.name}, ${locationSegment.country}`;
  }

  return locationSegment.name;
}
